// React Imports
import { useState, useEffect } from "react";

// External Libraries
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";

// Hooks & Helpers
import { fetchUserPathData, updateNameOnCertificate } from "sj-ui-toolkit/src/api";
import type { PassedBelt } from "sj-ui-toolkit/src/api/user";
import { useSmartNavigation } from "sj-ui-toolkit/src/helpers/navigation";
import { useToast } from "sj-ui-toolkit/src/hooks";
import { C, themeClasses as T } from "sj-ui-toolkit/src/atlas/utils/themeClasses";

// Local Components
import S from "./CertificatesTab.module.scss";
import { CertificateNameModal } from "./CertificateNameModal";
import { CertificateInfoColumn } from "./TableColumns/CertificateInfoColumn";
import { PathDurationColumn } from "./TableColumns/PathDurationColumn";
import { CompletedCountColumn } from "./TableColumns/CompletedCountColumn";
import { DownloadCertificateColumn } from "./TableColumns/DownloadCertificateColumn";

// Constants
import { INTERNAL_ROUTES } from "sj-ui-toolkit/src/constants/routes";
import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { useHideTimeSpent } from "sj-ui-toolkit/src/integrations";

interface CertificateUser {
  certFirstName?: string | null;
  certLastName?: string | null;
}

export function CertificatesTab() {
  useBreadcrumb({ label: "Certificates", route: "/profile/certificates", display: "page" });
  const hideTimeSpent = useHideTimeSpent();
  const [pathData, setPathData] = useState<PassedBelt[]>([]);
  const [loading, setLoading] = useState(true);
  const [isAddNameModalOpen, setIsAddNameModalOpen] = useState(false);
  const [userCertificateData, setUserCertificateData] = useState<CertificateUser | null>(null);
  const skeletonItems = Array(10).fill({}) as PassedBelt[];
  const { navigateTo } = useSmartNavigation();
  const { showToast } = useToast();
  const showEmptyState = !loading && pathData.length === 0;

  useEffect(() => {
    const loadPathData = async () => {
      try {
        const { passedBelts, user } = await fetchUserPathData();
        setUserCertificateData(user);
        setPathData(passedBelts);
      } catch (error) {
        console.error("Error fetching user path data:", error);
      } finally {
        setLoading(false);
      }
    };

    loadPathData();
  }, []);

  const getCertificateName = () => {
    const first = userCertificateData?.certFirstName?.trim();
    const last = userCertificateData?.certLastName?.trim();

    if (!first && !last) return "Add Your Name";

    return `${first} ${last} (Edit)`;
  };

  const handleSaveCertificateName = async (firstName: string, lastName: string) => {
    try {
      await updateNameOnCertificate(firstName, lastName);

      setUserCertificateData(prevData => ({
        ...prevData,
        certFirstName: firstName,
        certLastName: lastName,
      }));

      showToast({
        severity: "success",
        summary: "Success",
        detail: "Certificate name updated",
        life: 3000,
      });
      setIsAddNameModalOpen(false);
    } catch (error) {
      console.error("Error updating certificate name:", error);
      showToast({
        severity: "error",
        summary: "Error",
        detail: "Failed to update certificate name",
        life: 3000,
      });
    }
  };

  const hasCertificateName =
    !!userCertificateData?.certFirstName?.trim() && !!userCertificateData?.certLastName?.trim();

  return (
    <div className={S.root} data-testid="certificates-tab">
      <section aria-labelledby="path-certifications-heading">
        <h2 id="path-certifications-heading" className={C("atlas-h5", S.certificateHeader)}>
          Path Certifications
        </h2>
        {showEmptyState ? (
          <section aria-labelledby="empty-heading">
            <h3 id="empty-heading" className="atlas-h1 max-w-[450px]">
              You don’t have any certificates yet.
            </h3>
            <p className="body-sm text-muted-aaa">Complete a path to earn a certificate.</p>
            <Button
              className="mt-5"
              severity="secondary"
              label="View Catalog"
              onClick={() => navigateTo(INTERNAL_ROUTES.Learn.routes.catalog.route)}
              data-testid="certificates-view-catalog-button"
            />
          </section>
        ) : (
          <DataTable
            aria-label="Path certificates"
            key={`${userCertificateData?.certFirstName || ""}-${userCertificateData?.certLastName || ""}`}
            showHeaders={false}
            value={loading ? skeletonItems : pathData}
            paginator={!loading}
            rows={10}
            className={C("table-fixed w-full", T.bgElevation1)}
            rowClassName={() => "h-20"}
            rowsPerPageOptions={[10, 20, 50]}
            data-testid="certificates-table"
            pt={{
              paginator: {
                RPPDropdown: { select: { "aria-label": "Rows per page" } },
                root: { className: "mt-[12px]" },
              },
            }}
          >
            <Column
              field="name"
              body={(rowData: PassedBelt) => (
                <CertificateInfoColumn rowData={rowData} loading={loading} />
              )}
              className="w-2/5"
            />
            {!hideTimeSpent && (
              <Column
                field="duration"
                body={(rowData: PassedBelt) => (
                  <PathDurationColumn rowData={rowData} loading={loading} />
                )}
                className="w-1/5 text-right pr-[60px]"
              />
            )}
            <Column
              field="completedCount"
              body={(rowData: PassedBelt) => (
                <CompletedCountColumn rowData={rowData} loading={loading} />
              )}
              className="w-1/5"
            />
            <Column
              field="download"
              body={(rowData: PassedBelt) => (
                <DownloadCertificateColumn
                  rowData={rowData}
                  loading={loading}
                  certificateName={getCertificateName()}
                  onOpenName={() => setIsAddNameModalOpen(true)}
                />
              )}
              className="w-1/5 text-right"
            />
          </DataTable>
        )}
      </section>

      <CertificateNameModal
        isOpen={isAddNameModalOpen}
        onClose={() => setIsAddNameModalOpen(false)}
        onSave={handleSaveCertificateName}
        firstName={userCertificateData?.certFirstName || ""}
        lastName={userCertificateData?.certLastName || ""}
        hasCertificateName={hasCertificateName}
      />
    </div>
  );
}
